import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AccionesRol,
  ICategoriaUbicacion,
  IFilter,
  IPopulate,
  IQueryParam,
  IUbicacion,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { UbicacionService } from '../../../../auxiliares/servicios/http/ubicacion.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { CambiarClienteComponent } from '../../cambiar-cliente/cambiar-cliente.component';

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-ubicaciones',
  templateUrl: './listado-ubicaciones.component.html',
  styleUrl: './listado-ubicaciones.component.scss',
})
export class ListadoUbicacionesComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private service = inject(UbicacionService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  private paramsService = inject(ParamsService);
  private clienteJerarquia = inject(ClienteJerarquiaService);

  public categoria?: ICategoriaUbicacion;
  //// Tabla nueva
  public name = 'ListadoUbicacionesComponent';
  public datos?: IUbicacion[];

  public totalCount = 0;
  public columnas?: IColumnas<IUbicacion>[];
  //Subs
  public ubicacions$?: Subscription;
  //
  //Permisos (sólo se completan para las categorías que ya tienen AccionRol equivalente)
  private puedeCrear?: AccionesRol;
  private puedeEditar?: AccionesRol;
  private puedeEliminar?: AccionesRol;

  public botonCrear: ICrearBoton;

  public search: IRegExpSearch = {
    fields: ['identificacion'],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };

  /// Trae las ubicaciones de todos los clientes que cuelgan del elegido
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  public filtros: IFiltroTabla[] = [];
  //Query

  public populate: IPopulate = {
    path: 'cliente',
    select: 'nombre',
  };
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };
  // Listars

  public async listar(): Promise<void> {
    const filter: IFilter<IUbicacion> = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    filter.categoria =
      this.categoria === 'Vehiculos' //Si se accede desde la solapa de Vehiculos, mostrar ambas categorías
        ? { $in: ['Vehiculos', 'Zona'] }
        : this.categoria;
    this.query.filter = JSON.stringify(filter);

    //
    this.ubicacions$?.unsubscribe();
    this.ubicacions$ = this.lista2
      .ubicacions(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.datos = data.datos;
      });
  }

  // Acciones
  public async crear() {
    this.paramsService.setParams({
      ubicacion: null,
      categoria: this.categoria,
    });
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar(ubicacion: IUbicacion) {
    this.paramsService.setParams({
      ubicacion: ubicacion,
      categoria: this.categoria,
    });
    this.navigator.navigate(['editar', ubicacion._id], {
      relativeTo: this.route,
    });
  }

  public async cambiarCliente(ubicacion: IUbicacion) {
    const dialogRef = this.formularios.abrir(CambiarClienteComponent, {
      tamano: 'mediano',
      data: {
        entidad: ubicacion,
        tipoEntidad: 'Zona',
      },
    });
    dialogRef.afterClosed().subscribe(async (resultado) => {
      if (resultado) {
        await this.listar();
      }
    });
  }

  public async eliminar(ubicacion: IUbicacion) {
    const res = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Querés eliminar la ubicación ${ubicacion.identificacion}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(ubicacion._id!);
      this.helper.notifSuccess('Eliminado correctamente');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IUbicacion>[] = [
      // Identificacion
      {
        header: {
          label: 'Identificación',
          sortable: true,
        },
        row: {
          field: 'identificacion',
          noData: 'Sin identificación',
        },
      },
    ];

    // Cliente. Se muestra además la jerarquía de clientes en un tooltip: con
    // varios clientes de nombre parecido el nombre solo no alcanza para saber
    // de quién cuelga la ubicación.
    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        accionesGrouped: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () => !this.puedeEditarUbicacion(),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'drive_file_move',
            tooltip: 'Cambiar de cliente',
            click: (dato) => this.cambiarCliente(dato),
            // Sólo las zonas de vehículos: es el único listado donde el cambio
            // de cliente está probado (limpia configs de evento y recorridos)
            oculta: () => this.categoria !== 'Vehiculos',
            disabled: () => !this.puedeEditarUbicacion(),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () => !this.puedeEliminarUbicacion(),
          },
        ],
      },
    });
    this.columnas = columnas;
  }

  private puedeCrearUbicacion(): boolean {
    return !!this.puedeCrear && this.helper.tienePermiso(this.puedeCrear);
  }

  private puedeEditarUbicacion(): boolean {
    return !!this.puedeEditar && this.helper.tienePermiso(this.puedeEditar);
  }

  private puedeEliminarUbicacion(): boolean {
    return !!this.puedeEliminar && this.helper.tienePermiso(this.puedeEliminar);
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }

    this.route.params.subscribe(async (params) => {
      this.categoria = params?.['categoria'];

      switch (this.categoria) {
        case 'Activos':
          this.puedeCrear = 'Activos - Crear ubicaciones';
          this.puedeEditar = 'Activos - Editar ubicaciones';
          this.puedeEliminar = 'Activos - Eliminar ubicaciones';
          break;
        case 'Terminal':
          this.puedeCrear = 'Colectivos - Crear terminales';
          this.puedeEditar = 'Colectivos - Editar terminales';
          this.puedeEliminar = 'Colectivos - Eliminar terminales';
          break;
        case 'Vehiculos':
          this.puedeCrear = 'Vehículos - Crear zonas';
          this.puedeEditar = 'Vehículos - Editar zonas';
          this.puedeEliminar = 'Vehículos - Eliminar zonas';
          break;
        case 'Luminarias':
          this.puedeCrear = 'Luminarias - Crear zonas';
          this.puedeEditar = 'Luminarias - Editar zonas';
          this.puedeEliminar = 'Luminarias - Eliminar zonas';
          break;
        case 'Alarmas':
          this.puedeCrear = 'Alarmas - Crear zonas';
          this.puedeEditar = 'Alarmas - Editar zonas';
          this.puedeEliminar = 'Alarmas - Eliminar zonas';
          break;
        case 'Hospital':
          this.puedeCrear = 'Emergencias Médicas - Crear hospitales';
          this.puedeEditar = 'Emergencias Médicas - Editar hospitales';
          this.puedeEliminar = 'Emergencias Médicas - Eliminar hospitales';
          break;
        case 'Centro de Atención':
          this.puedeCrear = 'Emergencias Médicas - Crear centros médicos';
          this.puedeEditar = 'Emergencias Médicas - Editar centros médicos';
          this.puedeEliminar = 'Emergencias Médicas - Eliminar centros médicos';
          break;
        default:
          // Domicilio: es la dirección de una alarma
          this.puedeCrear = 'Alarmas - Crear';
          this.puedeEditar = 'Alarmas - Editar';
          this.puedeEliminar = 'Alarmas - Eliminar';
      }

      this.botonCrear = {
        mostrar: this.puedeCrearUbicacion(),
        tooltip: 'Crear ubicación',
        color: 'primary',
        tipo: 'material',
        icon: 'add',
        accion: () => this.crear(),
      };

      await this.listar();
    });
    this.setColumnas();
  }

  ngOnDestroy() {
    this.ubicacions$?.unsubscribe();
  }
}
